import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Form, Badge, Spinner, Modal, Alert, Row, Col, Card } from 'react-bootstrap';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaListUl } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const parseOptions = (rawOptions) => {
  if (!rawOptions) {
    return ['', ''];
  }

  if (Array.isArray(rawOptions)) {
    return rawOptions.length ? rawOptions : ['', ''];
  }

  if (typeof rawOptions === 'string') {
    const trimmed = rawOptions.trim();
    if (!trimmed) {
      return ['', ''];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch (error) {
      const lines = trimmed.split('\n').map((item) => item.trim()).filter(Boolean);
      if (lines.length > 0) {
        return lines;
      }
    }
  }

  return ['', ''];
};

const OrgSurveyQuestions = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'radio',
    options: ['', ''],
    is_required: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  useEffect(() => {
    fetchSurvey();
    fetchQuestions();
  }, [id]);

  const fetchSurvey = async () => {
    try {
      const response = await api.surveys.getById(id);
      setSurvey(response.data.data);
    } catch (error) {
      console.error('Error fetching survey:', error);
      toast.error('Failed to load survey');
      navigate('/organization/surveys');
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.surveys.getQuestions(id);
      const parsed = (response.data.data || []).map((question) => ({
        ...question,
        options: parseOptions(question.options),
      }));
      setQuestions(parsed);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setFormData({
        question_text: question.question_text || '',
        question_type: question.question_type || 'radio',
        options: parseOptions(question.options),
        is_required: Boolean(question.is_required),
      });
    } else {
      setEditingQuestion(null);
      setFormData({
        question_text: '',
        question_type: 'radio',
        options: ['', ''],
        is_required: false,
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingQuestion(null);
    setFormData({
      question_text: '',
      question_type: 'radio',
      options: ['', ''],
      is_required: false,
    });
    setFormErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleOptionChange = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.options];
      updated[index] = value;
      return { ...prev, options: updated };
    });
  };

  const addOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, ''],
    }));
  };

  const removeOption = (index) => {
    setFormData((prev) => {
      if (prev.options.length <= 2) {
        return prev;
      }
      return {
        ...prev,
        options: prev.options.filter((_, idx) => idx !== index),
      };
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.question_text.trim()) {
      errors.question_text = 'Question text is required';
    }

    if ((formData.question_type === 'radio' || formData.question_type === 'checkbox')) {
      const filledOptions = formData.options.filter((option) => option.trim());
      if (filledOptions.length < 2) {
        errors.options = 'At least two options are required';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setModalLoading(true);

      const payload = {
        survey_id: id,
        question_text: formData.question_text.trim(),
        question_type: formData.question_type,
        options:
          formData.question_type === 'radio' || formData.question_type === 'checkbox'
            ? formData.options.filter((option) => option.trim())
            : null,
        is_required: formData.is_required,
      };

      if (editingQuestion) {
        await api.surveys.updateQuestion(editingQuestion.id, payload);
        toast.success('Question updated successfully');
      } else {
        await api.surveys.createQuestion(payload);
        toast.success('Question created successfully');
      }

      handleCloseModal();
      fetchQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error(error.response?.data?.message || 'Failed to save question');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!questionToDelete) {
      return;
    }

    try {
      await api.surveys.deleteQuestion(questionToDelete.id);
      toast.success('Question deleted successfully');
      setShowDeleteModal(false);
      setQuestionToDelete(null);
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    }
  };

  const questionSummary = useMemo(() => ({
    total: questions.length,
    required: questions.filter((question) => question.is_required).length,
  }), [questions]);

  if (loading && !survey) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="flex justify-center items-center" style={{ height: '400px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="d-flex align-items-center gap-3 mb-4">
        <Button
          bsPrefix="custom-hamburger"
          onClick={() => navigate('/organization/surveys')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium custom-hamburger custom-hamburger-primary"
          title="Back to Surveys"
        >
          <FaArrowLeft className="w-5 h-5" /> Back
        </Button>
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Survey Questions
        </h5>
      </div>

      <Row className="g-3 mb-4">
        <Col xs={6} sm={6} md={3}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{questionSummary.total}</h3>
              <small className="text-white opacity-90">Total Questions</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} md={3}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-red-500 to-pink-500 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{questionSummary.required}</h3>
              <small className="text-white opacity-90">Required</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} className="d-flex align-items-center">
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => handleShowModal()}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            <FaPlus className="w-4 h-4" />
            <span className="whitespace-nowrap">Add Question</span>
          </Button>
        </Col>
      </Row>

      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-10 bg-white">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto"></div>
              <p className="mt-3 text-gray-600">Loading questions...</p>
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-10 bg-white">
              <FaListUl className="text-gray-300 mb-3" style={{ fontSize: '3rem' }} />
              <h5 className="text-gray-600 mb-2">No questions added yet</h5>
              <p className="text-muted text-sm">Click "Add Question" to get started.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3" style={{ width: '45%' }}>Question</th>
                    <th className="py-2 px-3" style={{ width: '20%' }}>Type</th>
                    <th className="py-2 px-3" style={{ width: '15%' }}>Required</th>
                    <th className="py-2 px-3 text-end" style={{ width: '20%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question, index) => (
                    <tr key={question.id} className="border-b border-purple-50 hover:bg-purple-50/60 transition-all">
                      <td className="py-2 px-3 align-middle">
                        <div className="fw-semibold text-gray-800 text-sm">{index + 1}. {question.question_text}</div>
                        {(question.question_type === 'radio' || question.question_type === 'checkbox') && (
                          <ul className="mb-0 mt-2 ps-3 text-muted small">
                            {question.options.map((option, idx) => (
                              <li key={idx}>{option}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <Badge bg="info" className="text-uppercase">
                          {question.question_type === 'radio'
                            ? 'Single Choice'
                            : question.question_type === 'checkbox'
                              ? 'Multiple Choice'
                              : 'Open Response'}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 align-middle">
                        {question.is_required ? (
                          <Badge bg="danger">Required</Badge>
                        ) : (
                          <Badge bg="secondary">Optional</Badge>
                        )}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="d-flex gap-1 justify-content-end">
                          <button
                            onClick={() => handleShowModal(question)}
                            className="text-blue-600 hover:bg-blue-100 p-2 rounded-lg transition-all"
                            title="Edit Question"
                          >
                            <FaEdit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {
                              setQuestionToDelete(question);
                              setShowDeleteModal(true);
                            }}
                            className="text-red-600 hover:bg-red-100 p-2 rounded-lg transition-all"
                            title="Delete Question"
                          >
                            <FaTrash className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingQuestion ? 'Edit Question' : 'Add Question'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Question Text <span className="text-red-500">*</span></label>
              <textarea
                rows={3}
                name="question_text"
                value={formData.question_text}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 rounded-lg border ${formErrors.question_text ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                placeholder="Enter the question text"
              />
              {formErrors.question_text && <p className="text-red-500 text-xs mt-1">{formErrors.question_text}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Question Type</label>
                <select
                  name="question_type"
                  value={formData.question_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                >
                  <option value="radio">Single Choice (Radio)</option>
                  <option value="checkbox">Multiple Choice (Checkbox)</option>
                  <option value="textarea">Open Response</option>
                </select>
              </div>
              <div className="d-flex align-items-end">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="questionRequired"
                    name="is_required"
                    checked={formData.is_required}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label text-sm font-semibold text-gray-700" htmlFor="questionRequired">
                    Required question
                  </label>
                </div>
              </div>
            </div>

            {(formData.question_type === 'radio' || formData.question_type === 'checkbox') && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Options <span className="text-red-500">*</span></label>
                {formData.options.map((option, index) => (
                  <div className="d-flex mb-2 gap-2" key={index}>
                    <input
                      type="text"
                      value={option}
                      onChange={(event) => handleOptionChange(index, event.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className={`flex-1 px-3 py-2 rounded-lg border ${index === 0 && formErrors.options ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                    />
                    {formData.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="text-red-600 hover:bg-red-100 p-2 rounded-lg transition-all"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {formErrors.options && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.options}</p>
                )}
                <Button
                  bsPrefix="custom-hamburger"
                  onClick={addOption}
                  className="mt-2 border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-3 py-2"
                >
                  <FaPlus className="w-4 h-4 me-2" />
                  Add Option
                </Button>
              </div>
            )}

            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleCloseModal} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button
                    bsPrefix="custom-hamburger"
                    type="submit"
                    disabled={modalLoading}
                    className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    {modalLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      editingQuestion ? 'Update Question' : 'Create Question'
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Modal.Body>
        </Form>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5 text-center py-10">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Question</h5>
          <p className="text-gray-600">Are you sure you want to delete this question? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={() => setShowDeleteModal(false)} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Delete Question
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgSurveyQuestions;
